declare global {
  interface Window {
    shopify?: {
      idToken(): Promise<string>;
      loading(isLoading: boolean): void;
    };
  }
}

/**
 * App Bridge installs `window.shopify` asynchronously, and React mounts as soon
 * as the bundle runs. Requests fired in that gap went out with no Authorization
 * header, came back 401, and the page kept its empty initial state — after
 * approving a plan the dashboard showed "Total Try-Ons 0" for a shop that had
 * nine, which reads as the purchase having wiped the data (#1824).
 *
 * Waiting is capped: outside the Shopify admin `window.shopify` never appears
 * and the request should just go out unauthenticated rather than hang.
 */
const APP_BRIDGE_TIMEOUT_MS = 5000;
const APP_BRIDGE_POLL_MS = 50;

async function waitForAppBridge(): Promise<void> {
  if (window.shopify) return;
  const deadline = Date.now() + APP_BRIDGE_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, APP_BRIDGE_POLL_MS));
    if (window.shopify) return;
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  await waitForAppBridge();
  if (!window.shopify) {
    return {};
  }
  try {
    const token = await window.shopify.idToken();
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
}

/**
 * A session token is short-lived, so one that was valid when the page loaded
 * can be rejected by the time a later request uses it. Asking App Bridge again
 * yields a fresh one, which is why a 401 is worth exactly one retry.
 */
async function requestWithRetry(path: string, init: RequestInit): Promise<Response> {
  const headers = await getAuthHeaders();
  let res = await fetch(path, { ...init, headers: { ...init.headers, ...headers } });
  if (res.status === 401 || res.status === 403) {
    const retryHeaders = await getAuthHeaders();
    if (retryHeaders.Authorization) {
      res = await fetch(path, { ...init, headers: { ...init.headers, ...retryHeaders } });
    }
  }
  return res;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await requestWithRetry(path, {});
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await requestWithRetry(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
  return res.json();
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await requestWithRetry(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PUT ${path} failed: ${res.status}`);
  return res.json();
}
